import { css } from '@emotion/react';
import {
  autoUpdate, flip, offset, shift, useFloating, useHover, useInteractions,
} from '@floating-ui/react';
import {
  type FC, useContext, useState,
} from 'react';
import ReactDataSheet from 'react-datasheet';
import type { GridElement } from '~/_shared/components/spreadsheet/spreadsheet.types';
import { SpreadsheetContext } from '~/_shared/components/spreadsheet/spreadsheetContext';
import { zIndexGeneral } from '~/_shared/constants/zIndexGeneral.enum';
import type { Theme } from '~/_shared/themes/theme.model';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import CellRendererProps = ReactDataSheet.CellRendererProps;

export const SPREADSHEET_CELL_HEIGHT = 24;
export const SPREADSHEET_CELL_MIN_WIDTH = 80;
export const SELECTED_OR_HIGHLIGHTED_CELL = 'SELECTED_OR_HIGHLIGHTED_CELL';
const MAX_CELL_CONTENT_LENGTH = 108;
const ELLIPSIS = '...';

export const cellContainerStyle = ({ width, minWidth, state }: {
  minWidth?: string | number;
  state?: ReadonlySet<SpreadsheetCellState>;
  width?: string | number;
}) => (theme: Theme) => {
  return css({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    border: `1px solid ${theme.borderColors.primary}`,
    width,
    minWidth,
    height: SPREADSHEET_CELL_HEIGHT,
    boxSizing: 'border-box',
    padding: '0 2px',
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    background: !state?.has(SpreadsheetCellState.disabled) ? 'inherit' : theme.backgroundColors.secondary,
    flexShrink: 0,
  }, highlightStyle(state)(theme));
};

export const cellTooltipStyle = (theme: Theme) => css({
  backgroundColor: theme.tooltipColors.backgroundColor,
  color: theme.tooltipColors.textColor,
  padding: '4px 8px',
  borderRadius: '3px',
  fontSize: '12px',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  zIndex: zIndexGeneral.PopperTooltip,
  maxWidth: '300px',
  wordBreak: 'break-word',
  pointerEvents: 'none',
});

const highlightStyle = (state?: ReadonlySet<SpreadsheetCellState>) => (theme: Theme) => {
  const isSelected = state?.has(SpreadsheetCellState.selected);
  const isHighlighted = state?.has(SpreadsheetCellState.highlighted);

  if (!isSelected && !isHighlighted) {
    return undefined;
  }

  const backgroundColor =
    (isSelected && isHighlighted && theme.backgroundColors.highlightHover)
    || (isSelected && theme.backgroundColors.secondaryHover)
    || (isHighlighted && theme.backgroundColors.highlight)
    || theme.backgroundColors.primary;

  const getBoxShadowProps = (yOffset: number) => `0 ${yOffset}px 0 1px inset ${theme.borderColors.activeItem}`;

  return css({
    backgroundColor,
    borderColor: theme.borderColors.activeItem,
    '&:after': {
      boxShadow: isHighlighted && !isSelected ? `${getBoxShadowProps(1)}, ${getBoxShadowProps(-1)}` : 'none',
      content: '" "',
      inset: 0,
      pointerEvents: 'none',
      position: 'absolute',
    },
  });
};

export enum SpreadsheetCellState {
  disabled = 'disabled',
  highlighted = 'highlight',
  selected = 'selected',
}

type SpreadsheetCellProps = CellRendererProps<GridElement, string>;

export const SpreadsheetCellComponent: FC<SpreadsheetCellProps> = (props) => {
  const spreadsheetContext = useContext(SpreadsheetContext);
  const [isOpen, setIsOpen] = useState(false);

  // Setup floating UI for tooltip (must be called before any early returns)
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top-start',
    middleware: [
      flip(),
      offset(-5),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: { open: 800, close: 0 },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

  if (!spreadsheetContext) {
    return null;
  }

  const { columns, data, highlightedRows, getColumnWidth } = spreadsheetContext;
  const columnId = columns[props.col]?.id;
  const rowId = data[props.row]?.rowId;

  const cellState = new Set([
    columns[props.col]?.disabled ? SpreadsheetCellState.disabled : undefined,
    notNullOrUndefined(rowId) && highlightedRows?.has(rowId) ? SpreadsheetCellState.highlighted : undefined,
    props.selected ? SpreadsheetCellState.selected : undefined,
  ].filter(notNullOrUndefined));

  const width = columnId !== undefined ? getColumnWidth(columnId) : undefined;

  const isDisabled = cellState.has(SpreadsheetCellState.disabled);
  const isSelectedOrHighlighted = cellState?.has(SpreadsheetCellState.selected) || cellState?.has(SpreadsheetCellState.highlighted);

  let content = props.cell.value?.toString() ?? '';
  content = content.length > MAX_CELL_CONTENT_LENGTH
    ? `${content.slice(0, MAX_CELL_CONTENT_LENGTH - ELLIPSIS.length)}${ELLIPSIS}`
    : content;

  return (
    <>
      <div
        ref={refs.setReference}
        className={`${props.className} ${isSelectedOrHighlighted ? SELECTED_OR_HIGHLIGHTED_CELL : ''}`}
        css={cellContainerStyle({
          state: cellState,
          minWidth: SPREADSHEET_CELL_MIN_WIDTH,
          width,
        })}
        data-disabled={isDisabled}
        onMouseOver={isDisabled ? undefined : props.onMouseOver}
        onMouseDown={isDisabled ? undefined : props.onMouseDown}
        onDoubleClick={isDisabled ? undefined : props.onDoubleClick}
        {...getReferenceProps()}
      >
        {props.children}
      </div>
      {isOpen && content && (
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          css={cellTooltipStyle}
          {...getFloatingProps()}
        >
          {content}
        </div>
      )}
    </>
  );
};
